import React, { useState, useCallback } from 'react'
import isEmpty from 'lodash/isEmpty'
import type { ConfirmDialogConfig, ConfirmDialogRequest } from '@ztechapps/common.hook'
import { ConfirmDialogContextProvider } from '@ztechapps/common.hook'
import { ShowIf } from '@ztechapps/webui.util'
import type { ConfirmModalIcon, ConfirmModalButtons } from './ConfirmModal'
import { ConfirmModal } from './ConfirmModal'
import { PromptContent } from './PromptContent'

type ConfirmDialogProviderProps = {
  children: React.ReactNode
}

export const ConfirmDialogProvider = ({ children }: ConfirmDialogProviderProps) => {
  const [current, setCurrent] = useState<ConfirmDialogRequest | null>(null)
  const [promptValue, setPromptValue] = useState('')

  const showDialog = useCallback(
    (config: ConfirmDialogConfig, promptValueRef?: React.MutableRefObject<string>): Promise<any> => {
      return new Promise((resolve) => {
        setPromptValue('')
        setCurrent({ ...config, resolve, promptValueRef })
      })
    },
    []
  )

  const handleConfirm = () => {
    if (!current) return

    const isPrompt = current.content === 'input'
    if (isPrompt) {
      const refValue = current.promptValueRef?.current
      const resolvedValue = !isEmpty(refValue) ? refValue : promptValue || true
      current.resolve(resolvedValue)
    } else {
      // Resolve the confirm button's configured value; default true keeps every
      // existing two-button dialog (which never sets confirm.value) unchanged.
      current.resolve(current.buttons?.confirm?.value ?? true)
    }
    setCurrent(null)
  }

  // Optional middle button (e.g. "NO") — resolves its own value (default false) so
  // callers can distinguish it from both confirm (truthy) and cancel (null).
  const handleNo = () => {
    if (!current) return
    current.resolve(current.buttons?.no?.value ?? false)
    setCurrent(null)
  }

  const handleCancel = () => {
    if (!current) return
    current.resolve(null)
    setCurrent(null)
  }

  const isPrompt = current?.content === 'input'
  const hasCustomComponent = !!current?.component
  const showPromptInput = isPrompt && !hasCustomComponent

  return (
    <ConfirmDialogContextProvider value={{ showDialog }}>
      {children}
      <ShowIf condition={!!current}>
        <ConfirmModal
          show={!!current}
          title={current?.title}
          text={current?.text}
          icon={current?.icon as ConfirmModalIcon}
          dangerMode={current?.dangerMode}
          buttons={
            current?.buttons
              ? ({
                  cancel: current.buttons.cancel,
                  no: current.buttons.no,
                  confirm: current.buttons.confirm,
                } as ConfirmModalButtons)
              : undefined
          }
          onConfirm={handleConfirm}
          onNo={handleNo}
          onCancel={handleCancel}
        >
          <>
            {current?.component}
            <ShowIf condition={showPromptInput}>
              <PromptContent onValueChange={setPromptValue} />
            </ShowIf>
          </>
        </ConfirmModal>
      </ShowIf>
    </ConfirmDialogContextProvider>
  )
}
